// Formulario de proveedor con el padrón DNIT —
// SPEC_ALTA_PROVEEDOR_DNIT_Y_CAMPOS_OPCIONALES_v0.1.md (en pos-graciela-api/docs).
//
// Lo del padrón se PROPONE: llena sólo lo vacío (RN-PV10). Obligatorios sólo
// el documento y la razón social (RN-PV2); el resto se valida en formato si
// viene (RN-PV5, RN-PV6), con las mismas reglas que el API.

export { documentoConsultable, avisoPadron } from './padronDnit.mjs'

const vacio = (v) => !String(v ?? '').trim()
const texto = (v) => (vacio(v) ? null : String(v).trim())
const limpiarDocumento = (v) => String(v ?? '').trim().replace(/[.\s]/g, '').toUpperCase()

export const formularioVacio = (documento = '', nombre = '') => ({
  documento: documento || '',
  ruc_padron: null,
  nombre: nombre || '',
  contacto: '',
  contacto_documento: '',
  telefono_celular: '',
  email: '',
  nombre_fantasia: '',
  direccion: '',
  condiciones_pago: ''
})

/** De la respuesta de `GET /api/providers/:id` al formulario. */
export const formularioDesdeProveedor = (p = {}) => ({
  documento: p.nro_documento || p.ruc || p.ci || '',
  ruc_padron: null,
  nombre: p.nombre || '',
  contacto: p.contacto || '',
  contacto_documento: p.contacto_documento || '',
  telefono_celular: p.telefono_celular || '',
  email: p.email || '',
  nombre_fantasia: p.nombre_fantasia || '',
  direccion: p.direccion || '',
  condiciones_pago: p.condiciones_pago || ''
})

/** Hay datos en «Más datos»: el bloque se abre en edición (PLAN D7). */
export const tieneMasDatos = (form) => ['nombre_fantasia', 'direccion', 'condiciones_pago'].some((k) => !vacio(form[k]))

// El padrón trae algunas razones sociales entre comillas: «"COOPERATIVA …"».
export const limpiarRazonSocial = (v) => String(v ?? '').trim().replace(/^"+|"+$/g, '').trim()

// RN-PV10: lo que completó el padrón no cuenta como escrito por el operador.
// `previo` es el valor que el padrón puso la vez anterior en ese campo: si el
// campo todavía lo tiene, se reemplaza; si el operador lo cambió, no se pisa.
const esDelOperador = (actual, previo) => !vacio(actual) && actual !== previo

/** RN-PV10: el RUC con DV y la razón social, sin pisar lo que escribió el operador. */
export const aplicarPadronProveedor = (form, dato, { nombrePrevio = null } = {}) => {
  const siguiente = { ...form }
  const base = limpiarDocumento(form.documento).split('-')[0]
  if (dato?.ruc && dato.ruc_sin_dv === base) {
    siguiente.documento = dato.ruc
    siguiente.ruc_padron = dato.ruc
  }
  const nombre = limpiarRazonSocial(dato?.nombre_completo || dato?.razon_social)
  if (nombre && !esDelOperador(form.nombre, nombrePrevio)) siguiente.nombre = nombre
  return siguiente
}

/** RN-PV3: el nombre del contacto, con la misma regla. */
export const aplicarPadronContacto = (form, dato, { nombrePrevio = null } = {}) => {
  const nombre = limpiarRazonSocial(dato?.nombre_completo)
  return nombre && !esDelOperador(form.contacto, nombrePrevio) ? { ...form, contacto: nombre } : form
}

/**
 * Al cambiar el documento, el nombre que había traído el padrón para el
 * documento anterior ya no corresponde: se vacía. Lo escrito a mano queda.
 */
export const olvidarDelPadron = (valor, previo) => (previo != null && valor === previo ? '' : valor)

export const normalizarCelular = (valor) => {
  if (vacio(valor)) return { ok: true, valor: null }
  let n = String(valor).trim().replace(/[\s\-()]/g, '')
  if (n.startsWith('+595')) n = `0${n.slice(4)}`
  else if (n.startsWith('595') && n.length >= 11) n = `0${n.slice(3)}`
  if (!/^\+?\d{7,15}$/.test(n)) return { ok: false, error: 'El celular va con números; se aceptan espacios, guiones y +595.' }
  return { ok: true, valor: n }
}

const CORREO_MAX = 50

/** RN-PV2, RN-PV5, RN-PV6 — mismas reglas que `domain/datosProveedor.js` del API. */
export const validarFormulario = (form) => {
  const errores = {}
  if (vacio(form.documento)) errores.documento = 'Falta el RUC o documento del proveedor.'
  if (vacio(form.nombre)) errores.nombre = 'Falta la razón social.'
  const c = normalizarCelular(form.telefono_celular)
  if (!c.ok) errores.telefono_celular = c.error
  if (!vacio(form.email) && (String(form.email).trim().length > CORREO_MAX || !/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(String(form.email).trim()))) {
    errores.email = `El correo no es válido (por ejemplo, nombre@dominio.com; hasta ${CORREO_MAX} caracteres).`
  }
  if (!vacio(form.direccion) && !/^[^\p{Cc}]+$/u.test(form.direccion)) errores.direccion = 'La dirección tiene caracteres no válidos.'
  return errores
}

/**
 * armarPayload — lo que va al API. Vacíos como `null` (RN-PV5). En edición, el
 * documento sólo viaja si cambió, para no tocar `ruc`/`ci` (CA-PV11).
 */
export const armarPayload = (form, { editMode = false, documentoOriginal = '' } = {}) => {
  const payload = {
    nombre: texto(form.nombre),
    contacto: texto(form.contacto),
    contacto_documento: texto(form.contacto_documento),
    telefono_celular: normalizarCelular(form.telefono_celular).valor ?? null,
    email: texto(form.email),
    nombre_fantasia: texto(form.nombre_fantasia),
    direccion: texto(form.direccion),
    condiciones_pago: texto(form.condiciones_pago)
  }
  const cambio = limpiarDocumento(form.documento) !== limpiarDocumento(documentoOriginal)
  if (!editMode || cambio) {
    payload.documento = texto(form.documento)
    if (form.ruc_padron) payload.ruc = form.ruc_padron
  }
  return payload
}
